import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { getImageUrl } from '../../api';
import { productSlug } from '../../utils/slug';
import AdminLayout from '../../components/AdminLayout';

export default function AdminInventory() {
  const [products, setProducts] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [search, setSearch] = useState('');
  const LIMIT = 50;
  const [adjustModal, setAdjustModal] = useState(null);
  const [logModal, setLogModal] = useState(null);
  const [logs, setLogs] = useState([]);
  const [adjustForm, setAdjustForm] = useState({ quantity: '', reason: '' });

  const load = async () => {
    const params = new URLSearchParams({ active_only: '0', limit: LIMIT, page });
    if (search) params.set('search', search);
    const [prodRes, lowRes] = await Promise.all([
      api.get(`/products?${params}`),
      api.get('/inventory/alerts/low-stock')
    ]);
    setProducts(prodRes.data.products);
    setPagination(prodRes.data.pagination);
    setLowStock(lowRes.data);
    setLoading(false);
  };

  useEffect(() => { setPage(1); }, [search]);
  useEffect(() => { load(); }, [search, page]);

  const viewLog = async (productId) => {
    const r = await api.get(`/inventory/${productId}/log`);
    setLogs(r.data);
    setLogModal(productId);
  };

  const handleAdjust = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/inventory/${adjustModal.id}/adjust`, {
        quantity: parseInt(adjustForm.quantity),
        reason: adjustForm.reason
      });
      setAdjustModal(null);
      setAdjustForm({ quantity: '', reason: '' });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed');
    }
  };

  return (
    <AdminLayout title="Inventory Management">
      {loading ? <div className="spinner" /> : (
        <>
          {lowStock.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ color: 'var(--danger)', marginBottom: 12 }}>⚠️ Low Stock Alerts ({lowStock.length})</h3>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>SKU</th><th>Product</th><th>Current Stock</th><th>Threshold</th><th>Action</th></tr></thead>
                  <tbody>
                    {lowStock.map(p => (
                      <tr key={p.id}>
                        <td style={{ fontFamily: 'monospace' }}>{p.sku}</td>
                        <td>{p.name}</td>
                        <td><span className={p.stock_quantity <= 0 ? 'out-of-stock' : 'low-stock'}>{p.stock_quantity}</span></td>
                        <td>{p.low_stock_threshold}</td>
                        <td><button className="btn btn-sm btn-primary" onClick={() => { setAdjustModal(p); setAdjustForm({ quantity: '', reason: 'Restock' }); }}>Restock</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <h3 style={{ marginBottom: 12 }}>All Inventory</h3>
          <div style={{ marginBottom: 12 }}>
            <input
              type="text" placeholder="Search products..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, width: 280 }}
            />
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th></th><th>SKU</th><th>Product</th><th>Category</th><th>Stock</th><th>Threshold</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {products.map(p => {
                  const imgUrl = getImageUrl(p.image_url);
                  return (
                  <tr key={p.id}>
                    <td>
                      {imgUrl ? (
                        <img src={imgUrl} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                      ) : (
                        <div style={{ width: 40, height: 40, background: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📦</div>
                      )}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.sku}</td>
                    <td><Link to={`/product/${productSlug(p.name, p.id)}`} style={{ fontWeight: 600 }}>{p.name}</Link></td>
                    <td>{p.category_name || '-'}</td>
                    <td style={{ fontSize: 16, fontWeight: 600 }}>{p.stock_quantity}</td>
                    <td>{p.low_stock_threshold}</td>
                    <td>
                      {p.stock_quantity <= 0 ? (
                        <span className="badge badge-cancelled">Out of Stock</span>
                      ) : p.stock_quantity <= p.low_stock_threshold ? (
                        <span className="badge badge-pending">Low Stock</span>
                      ) : (
                        <span className="badge badge-delivered">In Stock</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <Link to={`/product/${productSlug(p.name, p.id)}`} className="btn btn-sm btn-outline">View</Link>
                        <button className="btn btn-sm btn-primary" onClick={() => { setAdjustModal(p); setAdjustForm({ quantity: '', reason: '' }); }}>
                          Adjust
                        </button>
                        <button className="btn btn-sm btn-outline" onClick={() => viewLog(p.id)}>History</button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginTop: 20, flexWrap: 'wrap' }}>
              <button className="btn btn-sm btn-outline" onClick={() => setPage(1)} disabled={page === 1}>«</button>
              <button className="btn btn-sm btn-outline" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹ Prev</button>
              <span style={{ padding: '6px 12px', fontSize: 14 }}>Page {page} of {pagination.pages} ({pagination.total} products)</span>
              <button className="btn btn-sm btn-outline" onClick={() => setPage(p => p + 1)} disabled={page === pagination.pages}>Next ›</button>
              <button className="btn btn-sm btn-outline" onClick={() => setPage(pagination.pages)} disabled={page === pagination.pages}>»</button>
            </div>
          )}

          {adjustModal && (
            <div className="modal-overlay" onClick={() => setAdjustModal(null)}>
              <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 450 }}>
                <h2>Adjust Stock: {adjustModal.name}</h2>
                <p style={{ color: '#565959', marginBottom: 16 }}>Current stock: <strong>{adjustModal.stock_quantity}</strong></p>
                <form onSubmit={handleAdjust}>
                  <div className="form-group">
                    <label>Quantity Change (positive to add, negative to remove)</label>
                    <input type="number" value={adjustForm.quantity} onChange={e => setAdjustForm({ ...adjustForm, quantity: e.target.value })} required placeholder="e.g. 50 or -10" />
                  </div>
                  <div className="form-group">
                    <label>Reason *</label>
                    <select value={adjustForm.reason} onChange={e => setAdjustForm({ ...adjustForm, reason: e.target.value })} required>
                      <option value="">Select reason...</option>
                      <option value="Restock">Restock / New shipment</option>
                      <option value="Return">Customer return</option>
                      <option value="Damaged">Damaged / Write-off</option>
                      <option value="Correction">Inventory correction</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  {adjustForm.quantity && (
                    <div className="alert alert-success">
                      New stock will be: <strong>{adjustModal.stock_quantity + parseInt(adjustForm.quantity || 0)}</strong>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button type="submit" className="btn btn-primary">Apply</button>
                    <button type="button" className="btn btn-outline" onClick={() => setAdjustModal(null)}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {logModal && (
            <div className="modal-overlay" onClick={() => setLogModal(null)}>
              <div className="modal" onClick={e => e.stopPropagation()}>
                <h2>Inventory History</h2>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Date</th><th>Change</th><th>Reason</th><th>Reference</th></tr></thead>
                    <tbody>
                      {logs.map(l => (
                        <tr key={l.id}>
                          <td style={{ fontSize: 13 }}>{new Date(l.created_at).toLocaleString()}</td>
                          <td style={{ fontWeight: 600, color: l.change_quantity > 0 ? 'var(--success)' : 'var(--danger)' }}>
                            {l.change_quantity > 0 ? '+' : ''}{l.change_quantity}
                          </td>
                          <td>{l.reason}</td>
                          <td>{l.reference || '-'}</td>
                        </tr>
                      ))}
                      {logs.length === 0 && (
                        <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20, color: '#999' }}>No history</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <button className="btn btn-outline" style={{ marginTop: 16 }} onClick={() => setLogModal(null)}>Close</button>
              </div>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}
